import React, { useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import './LegacyReconciliationExpandedReportTable.css';
import type { Store as StoreType } from '../../types/services/auth';
import type { DailyExpandedAuditReport } from '../../services/storeAuditReportsService';
import { formatCurrency, formatNumber } from '../../utils/formatUtils';
import { computePackSoldAmount, formatPackTicketRange } from '../../utils/formatters/auditExpandedReportFormatters';
import { getStoreConfigurationFlags } from './utils/legacyAuditStoreConfiguration';
import LegacyReconciliationTableStateRow from './LegacyReconciliationTableStateRow';

type LegacyReconciliationExpandedReportTableProps = {
  report: DailyExpandedAuditReport | null;
  loading: boolean;
  error: string | null;
  store?: StoreType | null;
};

const LegacyReconciliationExpandedReportTable: React.FC<LegacyReconciliationExpandedReportTableProps> = React.memo(({
  report,
  loading,
  error,
  store,
}) => {
  const hasPosType = useMemo(() => getStoreConfigurationFlags(store).hasPosType, [store]);
  const games = useMemo(() => report?.games ?? [], [report?.games]);

  const flattenedRows = useMemo(() => {
    return games.flatMap((game, gameIdx) => {
      const packs = game.packs ?? [];
      const rowCount = Math.max(packs.length, 1);
      const stripeClass = gameIdx % 2 === 0 ? 'ler-row' : 'ler-row-alt';

      const varianceQty = Number(game.reconciliation_variance?.quantity_variance ?? 0);
      const varianceRevenue = Number(game.reconciliation_variance?.revenue_variance ?? 0);
      const rowColor = !hasPosType
        ? undefined
        : (varianceQty < 0 || varianceRevenue < 0)
          ? '#FF000014'
          : (varianceQty > 0 || varianceRevenue > 0)
            ? '#2c6ca014'
            : undefined;

      const costPerTicket = Number(game.cost_per_ticket ?? 0);
      const posSoldQty = Number((game.pos_totals as any)?.scratcher_count ?? 0);
      const posSalesAmount = Number((game.pos_totals as any)?.scratcher_sales ?? 0);

      return Array.from({ length: rowCount }).map((_, packIdx) => {
        const pack = packs[packIdx] ?? null;
        return {
          game,
          pack,
          isFirstInGame: packIdx === 0,
          rowSpan: rowCount,
          stripeClass,
          rowColor,
          costPerTicket,
          posSoldQty,
          posSalesAmount,
          varianceQty,
          varianceRevenue,
          packIdx,
        };
      });
    });
  }, [games, hasPosType]);

  const totals = useMemo(() => {
    return games.reduce(
      (acc, game) => {
        const auditSold = Number((game.audit_totals as any)?.total_sold ?? 0);
        const auditRevenue = Number((game.audit_totals as any)?.total_revenue ?? 0);
        const posSold = Number((game.pos_totals as any)?.scratcher_count ?? 0);
        const posSales = Number((game.pos_totals as any)?.scratcher_sales ?? 0);
        const varianceQty = Number(game.reconciliation_variance?.quantity_variance ?? 0);
        const varianceRevenue = Number(game.reconciliation_variance?.revenue_variance ?? 0);

        return {
          auditSold: acc.auditSold + (Number.isNaN(auditSold) ? 0 : auditSold),
          auditRevenue: acc.auditRevenue + (Number.isNaN(auditRevenue) ? 0 : auditRevenue),
          posSold: acc.posSold + (Number.isNaN(posSold) ? 0 : posSold),
          posSales: acc.posSales + (Number.isNaN(posSales) ? 0 : posSales),
          varianceQty: acc.varianceQty + (Number.isNaN(varianceQty) ? 0 : varianceQty),
          varianceRevenue: acc.varianceRevenue + (Number.isNaN(varianceRevenue) ? 0 : varianceRevenue),
        };
      },
      {
        auditSold: 0,
        auditRevenue: 0,
        posSold: 0,
        posSales: 0,
        varianceQty: 0,
        varianceRevenue: 0,
      }
    );
  }, [games]);

  const totalCols = hasPosType ? 9 : 5;

  return (
    <Box className="LegacyReconciliationExpandedReportTable">
      <TableContainer
        component={Paper}
        className="audit-detail-table"
        sx={{
          marginTop: 0,
          borderTopLeftRadius: 15,
          borderTopRightRadius: 15,
          '& th:last-of-type': { borderRight: 'none' },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                className="table-header game-info-cell"
                sx={{ width: '420px', minWidth: '420px' }}
              >
                <p className="parent-header-text">Game</p>
              </TableCell>
              <TableCell
                rowSpan={2}
                className="table-header audit-detail-pack-cell"
                sx={{ width: '160px', minWidth: '160px' }}
              >
                <p className="parent-header-text">Pack</p>
              </TableCell>

              <TableCell className="table-header" colSpan={3}>
                <p className="table-header-text text-align-center">Inventory</p>
                <p className="table-subheader-text">Actual Physical Count</p>
              </TableCell>

              {hasPosType ? (
                <>
                  <TableCell className="table-header" colSpan={2} sx={{ width: '200px', minWidth: '200px' }}>
                    <p className="table-header-text text-align-center">POS</p>
                    <p className="table-subheader-text">Reported Sales</p>
                  </TableCell>
                  <TableCell className="table-header" colSpan={2} sx={{ width: '240px', minWidth: '240px' }}>
                    <p className="table-header-text text-align-center">Reconciliation</p>
                    <p className="table-subheader-text">POS vs Inventory</p>
                  </TableCell>
                </>
              ) : null}
            </TableRow>

            <TableRow>
              {['Range', 'Sold Qty', 'Sold $'].map((text, idx) => (
                <TableCell
                  key={text}
                  className={`table-subheader${idx === 0 ? ' audit-detail-range-cell' : ''}`}
                  sx={{
                    width: idx === 0 ? '120px' : '100px',
                    minWidth: idx === 0 ? '120px' : '100px',
                    textAlign: idx === 0 ? 'left' : 'right',
                  }}
                >
                  {text}
                </TableCell>
              ))}

              {hasPosType ? (
                <>
                  {['Sold Qty', 'Sales $'].map((text) => (
                    <TableCell
                      key={`pos-${text}`}
                      className="table-subheader"
                      sx={{ width: '100px', minWidth: '100px' }}
                    >
                      {text}
                    </TableCell>
                  ))}
                  {['Variance Qty', 'Variance $'].map((text) => (
                    <TableCell
                      key={`recon-${text}`}
                      className="table-subheader"
                      sx={{ width: '100px', minWidth: '100px' }}
                    >
                      {text}
                    </TableCell>
                  ))}
                </>
              ) : null}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <LegacyReconciliationTableStateRow
                colSpan={totalCols}
                mode="loading"
                message="Loading audit data…"
              />
            ) : error ? (
              <LegacyReconciliationTableStateRow
                colSpan={totalCols}
                mode="error"
                message={error}
              />
            ) : flattenedRows.length === 0 ? (
              <LegacyReconciliationTableStateRow
                colSpan={totalCols}
                mode="empty"
                message="No audit data found"
              />
            ) : (
              <>
                {flattenedRows.map((row, rowIdx) => {
                  const packId = row.pack?.pack_id?.trim?.() ? row.pack.pack_id : '';
                  const range = row.pack
                    ? formatPackTicketRange(row.pack.starting_ticket_number, row.pack.ending_ticket_number)
                    : '-';
                  const qtySold = row.pack ? Number(row.pack.qty_sold ?? 0) : 0;
                  const soldAmount = row.pack ? computePackSoldAmount(qtySold, row.costPerTicket) : 0;
                  const rowKey = `${row.game.game_id}-${packId || 'no-pack'}-${row.packIdx}`;

                  return (
                    <TableRow
                      key={rowKey}
                      className={rowIdx % 2 === 0 ? 'audit-table__row--even' : 'audit-table__row--odd'}
                      style={row.rowColor ? { backgroundColor: row.rowColor } : undefined}
                    >
                      {row.isFirstInGame ? (
                        <TableCell rowSpan={row.rowSpan} className="table-data game-info-cell">
                          <div className="game-info-wrapper">
                            <div className="game-details-left">
                              <p className="game-name-new">{row.game.game_name || '—'}</p>
                              <p className="game-plu-new">{row.game.plu ?? '—'}</p>
                            </div>
                            <p className="game-cost-new">{formatCurrency(row.game.cost_per_ticket)}</p>
                          </div>
                        </TableCell>
                      ) : null}

                      <TableCell className="table-data audit-detail-pack-cell" sx={{ textAlign: 'left' }}>
                        <div className="pack-id-container">
                          <span className="pack-link">{row.game.game_id}</span>
                          {packId ? (
                            <>
                              <span>-</span>
                              <span className="pack-link">{packId}</span>
                            </>
                          ) : null}
                        </div>
                      </TableCell>

                      <TableCell className="table-data audit-detail-range-cell">
                        {range}
                      </TableCell>
                      <TableCell className="table-data" align="right">
                        {formatNumber(qtySold)}
                      </TableCell>
                      <TableCell className="table-data" align="right">
                        {formatCurrency(soldAmount)}
                      </TableCell>

                      {hasPosType ? (
                        <>
                          {row.isFirstInGame ? (
                            <>
                              <TableCell rowSpan={row.rowSpan} className="table-data" align="right" sx={{ verticalAlign: 'top' }}>
                                {formatNumber(row.posSoldQty)}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan} className="table-data" align="right" sx={{ verticalAlign: 'top' }}>
                                {formatCurrency(row.posSalesAmount)}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan} className="table-data" align="right" sx={{ verticalAlign: 'top' }}>
                                {row.varianceQty === 0 ? (
                                  <CheckCircle sx={{ color: '#34C759', fontSize: 20 }} />
                                ) : (
                                  formatNumber(row.varianceQty)
                                )}
                              </TableCell>
                              <TableCell rowSpan={row.rowSpan} className="table-data" align="right" sx={{ verticalAlign: 'top' }}>
                                {row.varianceRevenue === 0 ? (
                                  <CheckCircle sx={{ color: '#34C759', fontSize: 20 }} />
                                ) : (
                                  formatCurrency(row.varianceRevenue)
                                )}
                              </TableCell>
                            </>
                          ) : null}
                        </>
                      ) : null}
                    </TableRow>
                  );
                })}

                <TableRow className="totals-row">
                  <TableCell colSpan={2} className="table-header">
                    <p className="parent-header-text">Totals</p>
                  </TableCell>
                  <TableCell className="table-header" />
                  <TableCell className="table-header" align="right">
                    <p className="parent-header-text">{formatNumber(totals.auditSold)}</p>
                  </TableCell>
                  <TableCell className="table-header" align="right">
                    <p className="parent-header-text">{formatCurrency(totals.auditRevenue)}</p>
                  </TableCell>
                  {hasPosType ? (
                    <>
                      <TableCell className="table-header" align="right">
                        <p className="parent-header-text">{formatNumber(totals.posSold)}</p>
                      </TableCell>
                      <TableCell className="table-header" align="right">
                        <p className="parent-header-text">{formatCurrency(totals.posSales)}</p>
                      </TableCell>
                      <TableCell className="table-header" align="right">
                        <p className="parent-header-text">
                          {totals.varianceQty === 0 ? <CheckCircle sx={{ color: '#34C759', fontSize: 20 }} /> : formatNumber(totals.varianceQty)}
                        </p>
                      </TableCell>
                      <TableCell className="table-header" align="right">
                        <p className="parent-header-text">
                          {totals.varianceRevenue === 0 ? <CheckCircle sx={{ color: '#34C759', fontSize: 20 }} /> : formatCurrency(totals.varianceRevenue)}
                        </p>
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
});

LegacyReconciliationExpandedReportTable.displayName = 'LegacyReconciliationExpandedReportTable';

export default LegacyReconciliationExpandedReportTable;
